import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import AddAlertIcon from '@material-ui/icons/AddAlert';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { ActionButton } from '../components/ActionButton';
import { useControllerConnectionStatus } from '../Hooks/useControllerConnectionStatus';
import DialogWithEnterAction from '../components/DialogWithEnterAction';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';

import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { getMessageButtonStyle } from '../Styles/styles.js';
import { PrintDate } from '../Utils/utils.js';
import { alarmDisplayName, alarmValueFormat, formatAlarmValue } from '../Utils/alarm_utils';
import { alarmMapping } from '../Constants/constants';

import getStateManager from '../StateManager.js';
import { useSnapshot } from 'valtio';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    maxHeight: '40vh'
  },
  alarmEntry: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' }
  },
  resetAlarm: {
    marginLeft: theme.spacing(1),
    flexShrink: 0
  }
}));

function ConvertValue(id, value, controller) {
  const is_float = id in alarmMapping && alarmMapping[id]['type'] === 'float';
  const unit = id in alarmMapping ? alarmMapping[id]['unit'] : '';
  return formatAlarmValue(
    value,
    alarmValueFormat(id, { scale: is_float ? -1 : 0, unit }, controller)
  );
}

function GetAlarmIcon({ was_triggered }) {
  if (was_triggered) return <ReportProblemIcon style={{ color: 'red', marginRight: '0.5em' }} />;
  else return <CheckIcon style={{ color: 'green', marginRight: '0.5em' }} />;
}

function AlarmInfo({ id, name, was_triggered, controller, value, zone }) {
  return (
    <>
      <b>{controller.name}</b> {zone !== null && zone !== undefined ? `(${zone})` : ''}{' '}
      <i>{name}</i>
    </>
  );
}

function AlarmValue({ id, was_triggered, value, catalogEntry, identity, controller }) {
  if (identity) {
    if (!was_triggered || !catalogEntry?.value) return null;
    return formatAlarmValue(
      value,
      alarmValueFormat(catalogEntry.key, catalogEntry.value, controller)
    );
  }
  if (was_triggered && !(id in alarmMapping && alarmMapping[id].type === 'bool')) {
    const value_with_unit = ConvertValue(id, value, controller);
    return value_with_unit;
  }
  return <></>;
}

function AlarmStatus({ state, timestamp }) {
  const { t } = useTranslation();
  return (
    <>
      {state}: <small>({PrintDate(timestamp, 'l LTS', t('unknown_time'))})</small>
    </>
  );
}

export function AlarmEntry({
  alarm,
  controller = alarm.controller,
  index,
  resolveAlarm,
  zone,
  catalog = {}
}) {
  const { t, i18n } = useTranslation();
  const { status, disabledReason } = useControllerConnectionStatus(controller);
  const serverOwned = [1, 2].includes(Number(alarm.identity?.alarm_type_id));
  const writeAccess = useSnapshot(controller).access_rights?.alarms?.write === true;
  const connectionDisabled = !serverOwned && status !== 'good';
  const name = alarmDisplayName(alarm, catalog, i18n.language, t);
  const classes = useStyles();
  return (
    <ListItem key={'alarm_entry_' + index} className={classes.alarmEntry}>
      <ListItemIcon>
        <GetAlarmIcon was_triggered={true} />
      </ListItemIcon>
      <ListItemText
        primary={
          <AlarmInfo
            id={alarm.name}
            name={name}
            zone={zone}
            was_triggered={true}
            controller={alarm.controller}
          />
        }
        secondary={
          <AlarmValue
            id={alarm.name}
            identity={alarm.identity}
            catalogEntry={catalog[alarm.identity?.alarm_type_id]}
            controller={alarm.controller}
            was_triggered={true}
            value={alarm.value}
          />
        }
      />
      {alarm.identity && (
        <ActionButton
          component={IconButton}
          className={classes.resetAlarm}
          size="small"
          onClick={resolveAlarm}
          disabled={!writeAccess || connectionDisabled}
          title={
            connectionDisabled ? disabledReason : !writeAccess
              ? t('disabled_action_no_write_access') : t('alarm_clear_info')
          }
          aria-label={`${t('clear_alarm')}: ${name}`}
        >
          <CloseIcon fontSize="small" />
        </ActionButton>
      )}
    </ListItem>
  );
}

export function AlarmLogEntry({ logEntry, index, zone, catalog = {} }) {
  let { t, i18n } = useTranslation();
  const name = alarmDisplayName(logEntry, catalog, i18n.language, t);
  return (
    <ListItem key={'log_entry_' + index}>
      <ListItemIcon>
        <GetAlarmIcon was_triggered={logEntry.triggered} />
      </ListItemIcon>
      <ListItemText
        secondary={
          <AlarmStatus
            state={logEntry.triggered ? t('triggered') : t('resolved')}
            timestamp={logEntry.ts}
          />
        }
        primary={
          <AlarmInfo
            id={logEntry.id}
            name={name}
            was_triggered={logEntry.triggered}
            controller={logEntry.controller}
            value={logEntry.value}
            zone={zone}
          />
        }
      />
    </ListItem>
  );
}

export default function AlarmModal({ logged_in, ...other }) {
  let { t, i18n } = useTranslation();
  let { enqueueSnackbar } = useSnackbar();

  const stateManager = getStateManager(); // display values
  const store = stateManager.store(); // change values
  const alarms = useSnapshot(stateManager.alarms);
  const catalog = useSnapshot(stateManager.alarm_catalog_state);

  const classes = useStyles();
  // getModalStyle is not a pure function, we roll the style only on the first render
  const [open_alarms, setOpenAlarms] = React.useState(false);

  const ResolveAlarm = (alarm) => {
    if (!alarm.identity) return;
    const controller = stateManager.getControllerById(alarm.controller.id);
    const name = alarmDisplayName(alarm, catalog.entries, i18n.language, t);
    const serverOwned = [1, 2].includes(Number(alarm.identity.alarm_type_id));
    if (!serverOwned && controller?.connection?.status !== 'good') return;
    let sent = false;
    try {
      // Server-owned alarms must remain actionable while the controller is offline.
      sent = stateManager.wsManager.sendWSMsg(controller, 'resolve_alarm',
        { identity: alarm.identity }, false, false, serverOwned);
    } catch (_) { /* Report a closed/unavailable transport below. */ }
    enqueueSnackbar(`${t(sent ? 'alarm_clear_requested' : 'alarm_clear_unavailable')} ${name}`, {
      variant: sent ? 'info' : 'error'
    });
  };

  // Init alarms
  const handleOpenAlarms = () => {
    store.alarms.unreadAlarms = false;
    setOpenAlarms(true);
  };
  const handleCloseAlarms = () => {
    setOpenAlarms(false);
  };
  const active = alarms.activeAlarms.length > 0;
  const messageButton = getMessageButtonStyle(active, alarms.unreadAlarms);

  const [open_alarm_log, setOpenAlarmLog] = React.useState(false);
  const handleOpenAlarmLog = () => {
    setOpenAlarmLog(true);
  };
  const handleCloseAlarmLog = () => {
    setOpenAlarmLog(false);
  };

  if (logged_in) {
    return (
      <div style={{ zIndex: 2 }}>
        <AddAlertIcon style={messageButton} onClick={handleOpenAlarms}></AddAlertIcon>
        <DialogWithEnterAction
          open={open_alarms}
          onClose={handleCloseAlarms}
          onEnterAction={handleCloseAlarms}
          classes={{ paper: classes.dialogPaper }}
          fullWidth={true}
          maxWidth="sm"
          aria-labelledby="simple-modal-title"
        >
          <DialogTitle id="form-dialog-message-title">Alarms</DialogTitle>
          <DialogContent>
            <List>
              {alarms.activeAlarms.map((alarm, i) => (
                <AlarmEntry
                  alarm={alarm}
                  controller={stateManager.getControllerById(alarm.controller.id)}
                  catalog={catalog.entries ?? {}}
                  zone={
                    Number(alarm.zone) === 254
                      ? t('global')
                      : stateManager.getControllerById(alarm.controller.id).zones?.[alarm.zone]
                          ?.name
                  }
                  index={i}
                  resolveAlarm={() => ResolveAlarm(alarm)}
                />
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleOpenAlarmLog} color="primary">
              {t('alarm_log')}
            </Button>
            <Button onClick={handleCloseAlarms} color="primary">
              {t('close')}
            </Button>
          </DialogActions>
        </DialogWithEnterAction>

        <DialogWithEnterAction
          open={open_alarm_log}
          onClose={handleCloseAlarms}
          onEnterAction={handleCloseAlarmLog}
          classes={{ paper: classes.dialogPaper }}
          fullWidth={true}
          maxWidth="sm"
          aria-labelledby="simple-modal-title"
        >
          <DialogTitle id="form-dialog-message-title">Alarm Log</DialogTitle>
          <DialogContent>
            <List>
              {[...alarms.alarmsLog].reverse().map((logEntry, i) => (
                <AlarmLogEntry
                  logEntry={logEntry}
                  catalog={catalog.entries ?? {}}
                  index={i}
                  zone={
                    Number(logEntry.zone) === 254
                      ? t('global')
                      : stateManager.getControllerById(logEntry.controller.id).zones?.[
                          logEntry.zone
                        ]?.name
                  }
                />
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAlarmLog} color="primary">
              {t('close')}
            </Button>
          </DialogActions>
        </DialogWithEnterAction>
      </div>
    );
  } else {
    return <></>;
  }
}
